import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import HttpBackend from "i18next-http-backend";

export const SUPPORTED_LANGUAGES = ["en", "es"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export const DEFAULT_LANGUAGE: SupportedLanguage = "en";

export const NAMESPACES = [
  "common",
  "aiReceptionist",
  "landing",
  "pricing",
  "seo",
  "blog",
  "authors",
  "legal",
  "auth",
  "comparisons",
  "industries",
  "customerApp",
  "features",
  "demo",
  "cityLanding",
  "dashboard",
  "jobs",
  "customers",
  "technicians",
  "services",
  "equipment",
  "calendar",
  "profile",
  "billing",
  "invoices",
  "integrations",
  "locationTracking",
  "locationHistory",
  "performance",
  "technicianDashboard",
  "onboarding",
  "accountSetup",
  "inviteAccept",
  "invites",
  "subscription",
  "payInvoice",
  "trackJob",
  "helpQuickBooks",
  "status",
  "import",
  "referral",
  "partners",
] as const;

export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES as readonly string[] as string[],
    ns: ["common"],
    defaultNS: "common",
    load: "languageOnly",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
    },
    detection: {
      order: ["path", "localStorage", "navigator"],
      lookupFromPathIndex: 0,
      lookupLocalStorage: "workloop_lang",
      caches: ["localStorage"],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
  });

// Keep <html lang> in lockstep with the active language. When the declared
// lang ("en") disagreed with visibly-Spanish content, Chrome would silently
// auto-translate the page — and that DOM rewriting crashed React with a
// `Failed to execute 'insertBefore' on 'Node'` NotFoundError. Syncing the
// attribute removes the mismatch that triggers auto-translate in the first
// place (defense-in-depth alongside translate="no" on volatile subtrees).
if (typeof document !== "undefined") {
  const applyHtmlLang = (lng?: string) => {
    const short = (lng || i18n.language || DEFAULT_LANGUAGE).split("-")[0];
    document.documentElement.lang = short;
  };
  i18n.on("languageChanged", applyHtmlLang);
  applyHtmlLang();
}

export default i18n;
